/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  CELL_TEXT_PRIMARY_CLASS,
  CELL_TEXT_RIGHT_OVERFLOW_CLASS,
  code3Formatter,
  conditionalRowPostCreateDecorator,
  createDefaultGridModel,
  defaultCurrencyCellRendererDecorator,
  falsyAsEmptyCellRenderer,
  falsyCellRenderer,
  isCellValueNegative,
  noneCellRenderer,
  rendererIconByName,
  ROW_DIRTY_CLASS,
  ROW_FOOTER_LIGHT_CLASS,
  ROW_HIDDEN_CLASS,
  textOverlayRowPostCreate,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import {
  IS_REGION_AU_OR_NZ,
  NumberTypes,
  queryFormEditable,
  State,
  TextTypes,
} from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  buildEditableMultilineTextColumn,
  buildEditableNumberColumn,
  buildNumberColumn,
  cellRendererSelectorForPinnedRow,
  getRowIdFromData,
  GridModel,
  LayoutBasedGridDefinitionWithModel,
  typedValueFormatter,
} from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { ColDef, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { isNil } from 'lodash';
import {
  getQuoteVariationId,
  isQuoteVariation,
  isQuoteVariationTotal,
  QuoteVariation,
  quoteVariationFieldState,
  QuoteVariationOrTotal,
  validateQuoteVariationField,
} from '../../../resources/quote.model';
import { QuoteDetailsCommandService } from '../quote-details-command.service';
import { QuoteDetailsState } from '../quote-details.state';

@Injectable()
export class QuoteDetailsVariationGridDefinitionService extends LayoutBasedGridDefinitionWithModel {

  isAuOrNzRegion = IS_REGION_AU_OR_NZ;
  needRound = false;

  constructor(private translationService: TranslationService,
              private state: State<QuoteDetailsState>,
              private commands: QuoteDetailsCommandService) {
    super();
  }

  buildGridOptions(): GridOptions {
    return {
      getRowId: getRowIdFromData(getQuoteVariationId),
      rowClassRules: {
        [ROW_DIRTY_CLASS]: ({ data }: { data: QuoteVariation }) => !isNil(data.dirtyType),
        [ROW_FOOTER_LIGHT_CLASS]: ({ data }: { data: QuoteVariationOrTotal }) => isQuoteVariationTotal(data),
        [ROW_HIDDEN_CLASS]: ({data}: {data: QuoteVariation}) => data.grayOut,
      },
      processRowPostCreate: conditionalRowPostCreateDecorator(
        ({ node: { data } }) => isQuoteVariationTotal(data),
        textOverlayRowPostCreate(`${this.translationService.get('lbl_changes_total')}:`)),
    };
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel({
      getId: getQuoteVariationId,
      editable: this.state.select(queryFormEditable),
      validate: validateQuoteVariationField,
      getCellDef: quoteVariationFieldState,
      command: (entity, name, value) => this.commands.setVariationField(getQuoteVariationId(entity), name, value),
    });
  }

  hasDecimalPart(value: number): boolean {
    if (this.needRound) {
      return true;
    } else {
      if (value.toString().includes('.')) {
        this.needRound = true;
      }
      return value.toString().includes('.');
    }
  }

  buildColumnDefs(): Hash<ColDef> {
    const sortNumber = (a, b) => {
      if (!a && !!b) {
        return -1;
      }
      if (!a && !b) {
        return 0;
      }
      if (!!a && !b) {
        return 1;
      }
      const aNumber = parseFloat(a.toString().replace(',', ''));
      const bNumber = parseFloat(b.toString().replace(',', ''));
      return aNumber - bNumber;
    };
    const sort = (a, b) => {
      if (!a || !b) {
        return 0;
      }
      const aValue = a.value  || a;
      const bValue = b.value  || b;
      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }
      return 0;
    };
    return {
      'itemCode': {
        comparator: sort,
      },
      'description': {
        ...buildEditableMultilineTextColumn(
          TextTypes.Text,
          this.model.getCellAccessor('description'),
          { minRows: 5, maxRows: 14 }),
        cellRendererSelector: cellRendererSelectorForPinnedRow(noneCellRenderer),
        cellClass: CELL_TEXT_PRIMARY_CLASS,
        comparator: sort,
      },
      'costCenterCode': {
        valueFormatter: code3Formatter,
        cellClass: 'text-right',
        cellRendererSelector: cellRendererSelectorForPinnedRow(noneCellRenderer),
        comparator: sort,
      },
      'itemPrice': {
        ...buildNumberColumn(NumberTypes.Price),
        comparator: sortNumber,
      },
      'origQuantity': {
        ...buildNumberColumn(NumberTypes.Quantity),
        cellRenderer: falsyAsEmptyCellRenderer,
        comparator: sort,
      },
      'quantity': {
        ...buildEditableNumberColumn(NumberTypes.SignedQuantity, this.model.getCellAccessor('quantity')),
        cellRendererSelector: cellRendererSelectorForPinnedRow(noneCellRenderer),
        cellClass: CELL_TEXT_PRIMARY_CLASS,
        comparator: sort,
      },
      'debit': {
        valueGetter: ({ data }: { data: QuoteVariationOrTotal }) =>
          isQuoteVariationTotal(data) ? data.debit : (data.isCredit ? void 0 : data.price),
        ...buildNumberColumn(NumberTypes.Price),
        headerName: this.isAuOrNzRegion
          ? this.translationService.get('lbl_debit_dollar_ex_gst')
          : this.translationService.get('lbl_debit_dollar'),
        cellRendererSelector: cellRendererSelectorForPinnedRow(
          defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.Price))),
        cellClassRules: {
          [CELL_TEXT_RIGHT_OVERFLOW_CLASS]: ({ data }: { data: QuoteVariationOrTotal }) => isQuoteVariationTotal(data),
        },
        comparator: sortNumber,
      },
      'credit': {
        valueGetter: ({ data }: { data: QuoteVariationOrTotal }) =>
          isQuoteVariationTotal(data) ? data.credit
            : (data.isCredit ? data.price : void 0),
        ...buildNumberColumn(NumberTypes.Price),
        cellRendererSelector: cellRendererSelectorForPinnedRow(
          defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.Price))),
        headerName: this.isAuOrNzRegion
          ? this.translationService.get('lbl_credit_dollar_ex_gst')
          : this.translationService.get('lbl_credit_dollar'),
        cellClassRules: {
          'warn-color': (params: ValueFormatterParams) => isQuoteVariation(params.data),
        },
        comparator: sortNumber,
      },
      'customerPrice': {
        ...buildNumberColumn(NumberTypes.Price),
        valueGetter: ({ data }: { data: QuoteVariationOrTotal }) => {
          if (isQuoteVariationTotal(data)) {
            return data.totalPriceWithGst;
          }
          return data.priceWithGst ?
            ((this.hasDecimalPart(data.priceWithGst) || this.needRound) ?
              data.priceWithGst.toFixed(2) : data.priceWithGst)
            : '';
        },
        cellRendererSelector: cellRendererSelectorForPinnedRow(
          defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.Price))),
        cellClass: 'text-right',
        comparator: sortNumber,
      },
      'isInPriceBook': {
        cellRenderer: falsyCellRenderer(rendererIconByName('close')),
        cellRendererSelector: cellRendererSelectorForPinnedRow(noneCellRenderer),
        comparator: sort,
      },
      'hasMemo': {
        valueGetter: ({ data }: { data: QuoteVariation }) => isNotNil(data.memo) && data.memo.length > 0,
        cellRenderer: truthyCellRenderer(rendererIconByName('done')),
        comparator: sort,
      },
      'profit': {
        ...buildNumberColumn(NumberTypes.Price),
        cellRendererSelector: cellRendererSelectorForPinnedRow(
          defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.Price))),
        cellClassRules: {
          'warn-color': (params: ValueFormatterParams) => isQuoteVariation(params.data) && isCellValueNegative(params),
        },
        comparator: sortNumber,
      },
    };
  }
}
